import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom/cjs/react-router-dom";
import axios from "axios";
import {
  createFolderWithEmptyImages,
  getAllFolders,
  openDatabase,
} from "../../services/indexedDB";
import Walkthrough from "../Walkthrough/Walkthrough";
import "./AllFolders.css";
import "./FolderUpload.css";

const FOLDER_STEPS = [
  {
    selector: null,
    title: 'Event Folders 📁',
    description: "Here you can organise all photos for this event into folders. Let's walk through the key actions.",
    placement: 'center',
  },
  {
    selector: '.fu-breadcrumb',
    title: 'Navigate Back',
    description: 'Use the back arrow or click Events to return to your event list at any time.',
    placement: 'bottom',
  },
  {
    selector: '.btn-primary-create',
    title: 'Create a Folder',
    description: 'Create custom folders to organise photos by scene, moment, or location — e.g. Ceremony, Reception, Candids.',
    placement: 'bottom',
  },
  {
    selector: '.folders-grid li:first-child .folder-card-modern',
    title: 'All Photos',
    description: 'Open this folder to see every photo uploaded for the event, regardless of which folder they belong to.',
    placement: 'right',
  },
  {
    selector: '.folders-grid li:nth-child(2) .folder-card-modern',
    title: 'Selected Photos',
    description: 'Photos your client has marked as favourites appear here, making it easy to find and deliver the chosen shots.',
    placement: 'right',
  },
  {
    selector: '.folders-grid',
    title: 'Your Folders',
    description: 'Any folders you create will appear here. Click a folder to upload, view, or manage photos inside it.',
    placement: 'top',
  },
  {
    selector: '.hdr-settings-wrapper',
    title: 'Settings',
    description: 'Access event settings here — control download access, sharing permissions, watermark, logo, and more.',
    placement: 'bottom',
  },
];

// Simple UUID v4 generator
const uuidv4 = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export default function AllFolders() {
  const location = useLocation();
  const [folders, setFolders] = useState([]);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [tagSearch, setTagSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [creating, setCreating] = useState(false);

  const tagInputRef = useRef(null);

  // Get event data from navigation state
  const eventData = location.state || {};
  const { eventId, userId, eventTitle } = eventData;
  const effectiveEventId = eventId || localStorage.getItem('currentEventId') || 'all';
  const effectiveUserId = userId || localStorage.getItem('uid') || '';

  const searchQuery = new URLSearchParams(location.search).get('q') || '';

  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const apiKey = process.env.REACT_APP_BROWSER_API_KEY;

  const loadFolders = async () => {
    await openDatabase();
    const [idbFolders, apiFolders] = await Promise.all([
      getAllFolders().catch(() => []),
      (async () => {
        if (!effectiveEventId || effectiveEventId === 'all' || !effectiveUserId) {
          return [];
        }
        try {
          const res = await axios.get(`${baseURL}/gallery/folders`, {
            params: { event_id: effectiveEventId, user_id: effectiveUserId },
            headers: { 'x-api-key': apiKey },
          });
          const list = Array.isArray(res.data) ? res.data
            : Array.isArray(res.data?.folders) ? res.data.folders
            : [];
          return list.map(f => ({
            id: f.folder_id,
            value: { folderName: f.folder_name, tag: f.tags || null, eventId: f.event_id, userId: f.user_id },
            source: 'api',
          }));
        } catch (e) {
          return [];
        }
      })(),
    ]);


    // Filter IndexedDB folders to only those belonging to the current event
    const filteredIdbFolders = (effectiveEventId && effectiveEventId !== 'all')
      ? idbFolders.filter(f => !f.value?.eventId || f.value.eventId === effectiveEventId)
      : idbFolders;

    // Merge: IndexedDB is source of truth; fill in API-only folders not already present
    const idbIds = new Set(filteredIdbFolders.map(f => f.id));
    const idbNames = new Set(filteredIdbFolders.map(f => (f.value?.folderName || '').toLowerCase()));
    const apiOnly = apiFolders.filter(f =>
      !idbIds.has(f.id) && !idbNames.has((f.value?.folderName || '').toLowerCase())
    );
    setFolders([...filteredIdbFolders, ...apiOnly]);
  };

  useEffect(() => {
    loadFolders();
  }, []);

  const openCreateModal = async () => {
    setNewFolderName("");
    setTagSearch("");
    setSelectedTags([]);
    setShowTagDropdown(false);
    setShowModal(true);

    setTagsLoading(true);
    try {
      const res = await axios.get(`${baseURL}/tags`, {
        headers: { "x-api-key": process.env.REACT_APP_BROWSER_API_KEY },
      });
      const data = res.data;
      // Handle plain array, or nested under common keys
      const tagList = Array.isArray(data)
        ? data
        : Array.isArray(data?.tags)
        ? data.tags
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.body)
        ? data.body
        : typeof data?.body === "string"
        ? JSON.parse(data.body)
        : [];
      setTags(tagList);
    } catch (err) {
      setTags([]);
    } finally {
      setTagsLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setNewFolderName("");
    setTagSearch("");
    setSelectedTags([]);
    setShowTagDropdown(false);
  };

  const getTagLabel = (tag) => typeof tag === "string" ? tag : tag.name || tag.label || String(tag);

  const filteredTags = tags.filter((tag) => {
    const label = getTagLabel(tag);
    const alreadySelected = selectedTags.some((t) => getTagLabel(t) === label);
    return !alreadySelected && label.toLowerCase().includes(tagSearch.toLowerCase());
  });

  const handleSelectTag = (tag) => {
    setSelectedTags((prev) => [...prev, tag]);
    setTagSearch("");
    setShowTagDropdown(true);
    tagInputRef.current?.focus();
  };

  const handleRemoveTag = (tag) => {
    setSelectedTags((prev) => prev.filter((t) => getTagLabel(t) !== getTagLabel(tag)));
  };

  const handleAddFolder = async () => {
    if (!newFolderName.trim()) {
      alert("Folder name cannot be empty");
      return;
    }
    setCreating(true);
    try {
      const folderID = uuidv4();
      const tagValue = selectedTags.length > 0
        ? selectedTags.map(getTagLabel)
        : null;

      await createFolderWithEmptyImages(folderID, newFolderName, effectiveEventId, effectiveUserId, null, false, tagValue);

      // Save to backend
      try {
        const payload = {
          folder_id: folderID,
          event_id: effectiveEventId,
          folder_name: newFolderName.trim(),
          user_id: effectiveUserId,
          ...(tagValue && { tags: tagValue }),
        };
        await axios.post(`${baseURL}/gallery/folders`, payload, {
          headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey },
        });
      } catch (apiErr) {
      }

      await loadFolders();
      closeModal();
    } catch (error) {
      alert(`Failed to create folder: ${error.message || 'Unknown error'}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="folders-page-container">

      {/* ── Breadcrumb ── */}
      <div className="fu-breadcrumb">
        <button className="fu-breadcrumb-back" onClick={() => { window.__appAllowBack = true; window.history.back(); }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <Link to="/eventlist" className="fu-breadcrumb-link">Events</Link>
        <span className="fu-breadcrumb-sep">›</span>
        <span className="fu-breadcrumb-current">All Folders</span>
      </div>

      {/* ── Title Block ── */}
      <div className="folders-title-block">
        <h1>
          📁 <span>Event Folders</span>
        </h1>
        <p className="folders-subtitle">
          {eventTitle ? `Managing folders for ${eventTitle}` : "Organize your event files into smart folders"}
        </p>
      </div>

      {/* ── Action Bar ── */}
      <div className="folders-action-bar">
        <div className="folder-create-group">
          <button className="btn-primary-create" onClick={openCreateModal}>
            + Create Folder
          </button>
        </div>
        <button
          className="af-tour-btn"
          onClick={() => setShowWalkthrough(true)}
          title="Page tour"
        >?</button>
      </div>

      {/* ── Folders Grid ── */}
      <ul className="folders-grid">
        {/* Static "All" folder — always visible */}
        <li>
          <Link to={`/folders/${effectiveEventId}/All`} className="folder-card-wrapper">
            <div className="folder-card-modern">
              <svg className="folder-icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M54.5 18H29L24.5 12H9.5C6.46 12 4 14.46 4 17.5V46.5C4 49.54 6.46 52 9.5 52H54.5C57.54 52 60 49.54 60 46.5V23.5C60 20.46 57.54 18 54.5 18Z" fill="url(#paint0_linear_all)"/>
                <path d="M54.5 21H9.5C6.46 21 4 23.46 4 26.5V46.5C4 49.54 6.46 52 9.5 52H54.5C57.54 52 60 49.54 60 46.5V26.5C60 23.46 57.54 21 54.5 21Z" fill="url(#paint1_linear_all)"/>
                <defs>
                  <linearGradient id="paint0_linear_all" x1="4" y1="12" x2="60" y2="52" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#F80353" stopOpacity="0.4"/>
                    <stop offset="1" stopColor="#F80353" stopOpacity="0.1"/>
                  </linearGradient>
                  <linearGradient id="paint1_linear_all" x1="4" y1="21" x2="60" y2="52" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#F80353" stopOpacity="0.15"/>
                    <stop offset="1" stopColor="#F80353" stopOpacity="0.02"/>
                  </linearGradient>
                </defs>
              </svg>
              <h3 className="folder-name-modern">All</h3>
              <p className="folder-meta">All Event Photos</p>
            </div>
          </Link>
        </li>

        {/* Static "Selected" folder — always visible */}
        <li>
          <Link to={`/folders/${effectiveEventId}/Selected`} className="folder-card-wrapper">
            <div className="folder-card-modern">
              <svg className="folder-icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M54.5 18H29L24.5 12H9.5C6.46 12 4 14.46 4 17.5V46.5C4 49.54 6.46 52 9.5 52H54.5C57.54 52 60 49.54 60 46.5V23.5C60 20.46 57.54 18 54.5 18Z" fill="url(#paint0_linear_sel)"/>
                <path d="M54.5 21H9.5C6.46 21 4 23.46 4 26.5V46.5C4 49.54 6.46 52 9.5 52H54.5C57.54 52 60 49.54 60 46.5V26.5C60 23.46 57.54 21 54.5 21Z" fill="url(#paint1_linear_sel)"/>
                <path d="M22 32l6 6 14-12" stroke="#F80353" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"/>
                <defs>
                  <linearGradient id="paint0_linear_sel" x1="4" y1="12" x2="60" y2="52" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#F80353" stopOpacity="0.4"/>
                    <stop offset="1" stopColor="#F80353" stopOpacity="0.1"/>
                  </linearGradient>
                  <linearGradient id="paint1_linear_sel" x1="4" y1="21" x2="60" y2="52" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#F80353" stopOpacity="0.15"/>
                    <stop offset="1" stopColor="#F80353" stopOpacity="0.02"/>
                  </linearGradient>
                </defs>
              </svg>
              <h3 className="folder-name-modern">Selected</h3>
              <p className="folder-meta">Selected Photos</p>
            </div>
          </Link>
        </li>

        {/* User-created folders */}
        {folders
          .filter(folder => {
            const folderData = folder.value || folder;
            if (folderData.isSubfolder || folderData.parentFolderId) return false;
            if (searchQuery) {
              const name = (folderData.folderName || '').toLowerCase();
              return name.includes(searchQuery.toLowerCase());
            }
            return true;
          })
          .map((folder) => (
            <li key={folder.id}>
              <Link to={`/folders/${folder.id}`} className="folder-card-wrapper">
                <div className="folder-card-modern">
                  <svg className="folder-icon-svg" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M54.5 18H29L24.5 12H9.5C6.46 12 4 14.46 4 17.5V46.5C4 49.54 6.46 52 9.5 52H54.5C57.54 52 60 49.54 60 46.5V23.5C60 20.46 57.54 18 54.5 18Z" fill="url(#paint0_linear)"/>
                    <path d="M54.5 21H9.5C6.46 21 4 23.46 4 26.5V46.5C4 49.54 6.46 52 9.5 52H54.5C57.54 52 60 49.54 60 46.5V26.5C60 23.46 57.54 21 54.5 21Z" fill="url(#paint1_linear)"/>
                    <defs>
                      <linearGradient id="paint0_linear" x1="4" y1="12" x2="60" y2="52" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#F80353" stopOpacity="0.4"/>
                        <stop offset="1" stopColor="#F80353" stopOpacity="0.1"/>
                      </linearGradient>
                      <linearGradient id="paint1_linear" x1="4" y1="21" x2="60" y2="52" gradientUnits="userSpaceOnUse">
                        <stop stopColor="#F80353" stopOpacity="0.15"/>
                        <stop offset="1" stopColor="#F80353" stopOpacity="0.02"/>
                      </linearGradient>
                    </defs>
                  </svg>
                  <h3 className="folder-name-modern">
                    {folder.value?.folderName || "Unnamed Folder"}
                  </h3>
                  {folder.value?.tag && (
                    <div className="folder-tag-badges">
                      {(Array.isArray(folder.value.tag) ? folder.value.tag : [folder.value.tag]).map((t, i) => (
                        <span key={i} className="folder-tag-badge">{t}</span>
                      ))}
                    </div>
                  )}
                  <p className="folder-meta">Event Assets</p>
                </div>
              </Link>
            </li>
          ))}
      </ul>

      {showWalkthrough && (
        <Walkthrough
          steps={FOLDER_STEPS}
          onDone={() => {
            setShowWalkthrough(false);
          }}
        />
      )}



      {/* ── Create Folder Modal ── */}
      {showModal && (
        <div className="cf-overlay" onClick={closeModal}>
          <div className="cf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cf-modal-header">
              <h2>Create New Folder</h2>
              <button className="cf-close-btn" onClick={closeModal}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="cf-modal-body">
              {/* Folder Name */}
              <div className="cf-field">
                <label className="cf-label">Folder Name <span className="cf-required">*</span></label>
                <input
                  className="cf-input"
                  type="text"
                  placeholder="e.g. Wedding Ceremony"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleAddFolder(); }}
                  autoFocus
                />
              </div>

              {/* Tag Autocomplete */}
              <div className="cf-field">
                <label className="cf-label">Tag <span className="cf-optional">(optional)</span></label>
                <div className="cf-autocomplete-wrapper">
                  {/* Selected tags as pills */}
                  {selectedTags.length > 0 && (
                    <div className="cf-selected-tags-row">
                      {selectedTags.map((tag, i) => (
                        <span key={i} className="cf-tag-pill">
                          {getTagLabel(tag)}
                          <button
                            className="cf-tag-pill-remove"
                            onMouseDown={(e) => { e.preventDefault(); handleRemoveTag(tag); }}
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  <input
                    ref={tagInputRef}
                    className="cf-input"
                    type="text"
                    placeholder={tagsLoading ? "Loading tags..." : selectedTags.length > 0 ? "Add more tags..." : "Search and select tags..."}
                    value={tagSearch}
                    disabled={tagsLoading}
                    onChange={(e) => {
                      setTagSearch(e.target.value);
                      setShowTagDropdown(true);
                    }}
                    onFocus={() => setShowTagDropdown(true)}
                    onBlur={() => setTimeout(() => setShowTagDropdown(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && tagSearch.trim()) {
                        e.preventDefault();
                        const trimmed = tagSearch.trim();
                        const alreadySelected = selectedTags.some(t => getTagLabel(t) === trimmed);
                        if (!alreadySelected) {
                          handleSelectTag(trimmed);
                        }
                        setShowTagDropdown(false);
                      }
                    }}
                  />

                  {tagSearch.trim() && !filteredTags.some(t => getTagLabel(t).toLowerCase() === tagSearch.trim().toLowerCase()) && (
                    <p className="cf-tag-hint">Press <kbd>Enter</kbd> to add "{tagSearch.trim()}" as a new tag</p>
                  )}

                  {showTagDropdown && !tagsLoading && (
                    <ul className="cf-dropdown">
                      {filteredTags.length === 0 ? (
                        <li className="cf-dropdown-empty">No tags found</li>
                      ) : (
                        filteredTags.map((tag, i) => (
                          <li
                            key={i}
                            className="cf-dropdown-item"
                            onMouseDown={() => handleSelectTag(tag)}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                              <line x1="7" y1="7" x2="7.01" y2="7"></line>
                            </svg>
                            {getTagLabel(tag)}
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <div className="cf-modal-footer">
              <button className="cf-btn-cancel" onClick={closeModal}>Cancel</button>
              <button
                className="cf-btn-create"
                onClick={handleAddFolder}
                disabled={creating || !newFolderName.trim()}
              >
                {creating ? "Creating..." : "+ Create Folder"}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
